/**
 * A escolha "usar a classificação da planilha" do envio — a lógica pura
 * por trás do pop-up "Configurações avançadas" (2026-10-07).
 *
 * O padrão é TUDO pelo Reputaition (lista vazia). A pessoa liga, uma a
 * uma, as dimensões que quer tomar da planilha, e só as que o template do
 * arquivo MAPEIA estão disponíveis — por isso trocar o template do
 * arquivo pode derrubar escolhas que deixaram de fazer sentido, e é aqui
 * que isso é decidido (e testado), não no JSX.
 */

import type { UploadTemplateInfo } from "@/shared/api/api-client";
import {
  IMPORTABLE_DIMENSIONS,
  canonicalFieldLabel,
  isImportableDimension,
  type ImportableDimension
} from "@/shared/vocabulary/canonical-fields";

/** As dimensões que o template mapeia, na ordem do vocabulário (a que o
 *  servidor devolve). Valor que o front não conhece é ignorado: oferecer
 *  uma opção que o envio não sabe mandar seria um 422 no clique. */
export function availableDimensions(
  template: Pick<UploadTemplateInfo, "importable_dimensions"> | undefined | null
): ImportableDimension[] {
  const offered = new Set(template?.importable_dimensions ?? []);
  return IMPORTABLE_DIMENSIONS.filter(d => offered.has(d) && isImportableDimension(d));
}

/** Liga ou desliga uma dimensão, mantendo a ordem do vocabulário e sem
 *  repetição — a lista que viaja é sempre igual pra mesma escolha. */
export function toggleDimension(
  selected: readonly ImportableDimension[],
  dimension: ImportableDimension,
  on: boolean
): ImportableDimension[] {
  const next = new Set(selected);
  if (on) next.add(dimension);
  else next.delete(dimension);
  return IMPORTABLE_DIMENSIONS.filter(d => next.has(d));
}

/** Ao trocar o template do arquivo, fica só o que o novo template mapeia.
 *  Sem isso o envio mandaria uma dimensão que o servidor recusa. */
export function pruneDimensions(
  selected: readonly ImportableDimension[],
  available: readonly ImportableDimension[]
): ImportableDimension[] {
  return selected.filter(d => available.includes(d));
}

/** A bolinha vermelha do botão: há alguma coisa fora do padrão. */
export function hasImportedChoice(selected: readonly ImportableDimension[]): boolean {
  return selected.length > 0;
}

/** "Polaridade, Temas" — pro resumo ao lado do botão. */
export function describeImported(selected: readonly ImportableDimension[]): string {
  return selected.map(canonicalFieldLabel).join(", ");
}
